import React, { useState, useEffect } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { fetchData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { Refresh, FileDownload } from '@mui/icons-material';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';


function DeliveryStockReport() {
    const [dataItem, setDataItem] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [loading, setLoading] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState('Loading...');
    const { isAuthenticated, companyId, branchId, token, logout, username, companyName } = useAuth();

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) {
            logout();
            return;
        }
        setFromDate(getFormattedStartDate());
        setToDate(getFormattedCurrentDate());
    }, [isAuthenticated, token, logout]);

    useEffect(() => {
        if (fromDate && toDate && companyId && branchId && token) {
            fetchTableData();
        }
    }, [fromDate, toDate, companyId, branchId, token]);

    const getFormattedCurrentDate = () => {
        const currentDate = new Date();
        return currentDate.toISOString().split('T')[0];
    };

    const getFormattedStartDate = () => {
        const today = new Date();
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, '0');
        return `${year}-${month}-01`;
    };

    const fetchTableData = async () => {
        setDataItem([]);
        
        // Delay showing loading overlay to prevent flash on quick loads
        const loadingTimeout = setTimeout(() => {
            setLoading(true);
            setLoadingMessage('Loading delivery stock report...');
        }, 300);
        
        try {
            const response = await fetchData('ReportApi', `/DeliveryStockReport/${companyId}/${branchId}/${fromDate}/${toDate}`, token);
            if (response.responseCode === '1111') {
                setDataItem(response.response_data);
            }
        } catch (error) {
            console.error('Error fetching Delivery Stock Report:', error);
        } finally {
            clearTimeout(loadingTimeout);
            setLoading(false);
        }
    };

    const handleFromDateChange = (e) => {
        const selectedDate = e.target.value;
        setFromDate(selectedDate);
    };

    const handleToDateChange = (e) => {
        const selectedDate = e.target.value;
        setToDate(selectedDate);
    };

    const filteredDataItem = dataItem.filter((item) =>
        Object.values(item).some((value) =>
            String(value).toLowerCase().includes(searchQuery.toLowerCase())
        )
    );

    const formatDate = (dateString) => {
        const options = { year: 'numeric', month: '2-digit', day: '2-digit' };
        return new Date(dateString).toLocaleDateString('en-GB', options);
    };

    const handleExportExcel = async () => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Delivery Stock Report');
        worksheet.views = [
            { state: 'frozen', xSplit: 0, ySplit: 3 }
        ];

        const now = new Date();
        const formattedDateTime = `${now.toLocaleDateString()} ${now.toLocaleTimeString()}`;
        const fileName = `Delivery_Stock_Report_From_${formatDate(fromDate).replace(/\//g, '-')}_To_${formatDate(toDate).replace(/\//g, '-')}.xlsx`;

        const thinBorder = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' }
        };

        // Company Name Row
        worksheet.mergeCells('A1:K1');
        const companyCell = worksheet.getCell('A1');
        companyCell.value = companyName || 'Company Name';
        companyCell.font = { bold: true, size: 14 };
        companyCell.alignment = { vertical: 'middle', horizontal: 'center' };

        // Title row
        worksheet.mergeCells('A2:K2');
        const titleCell = worksheet.getCell('A2');
        titleCell.value = `Delivery Stock Report - From: ${formatDate(fromDate)} To: ${formatDate(toDate)} | Generated On: ${formattedDateTime} By: ${username}`;
        titleCell.font = { bold: true, size: 10 };
        titleCell.alignment = { vertical: 'middle', horizontal: 'left' };

        // Header row
        const headers = ['Sl.No', 'Name', 'Total Delivery Out Qty', 'Total Delivery In Qty', 'Pending Qty', 'Total Good Qty', 'Total Damaged Qty', 'Total Stock'];
        const headerRow = worksheet.addRow(headers);

        headerRow.eachCell((cell) => {
            cell.font = { bold: true };
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF667eea' } };
            cell.alignment = { vertical: 'middle', horizontal: 'center' };
            cell.border = thinBorder;
        });

        // Add data rows
        filteredDataItem.forEach((rowData, index) => {
            const row = worksheet.addRow([
                index + 1,
                rowData.Name || '',
                rowData.TotalDeliveryOutQty || 0,
                rowData.TotalDeliveryInQty || 0,
                rowData.PendingQty || 0,
                rowData.TotalGoodQty || 0,
                rowData.TotalDamagedQty || 0,
                rowData.TotalStock || 0
            ]);

            row.eachCell((cell, colNumber) => {
                if (colNumber > 2) {
                    cell.alignment = { vertical: 'middle', horizontal: 'right' };
                    cell.numFmt = '#,##0';
                } else {
                    cell.alignment = { vertical: 'middle', horizontal: 'left' };
                }
                cell.border = thinBorder;
            });
        });

        // Totals row
        const totals = filteredDataItem.reduce((acc, d) => ({
            TotalDeliveryOutQty: acc.TotalDeliveryOutQty + (d.TotalDeliveryOutQty || 0),
            TotalDeliveryInQty: acc.TotalDeliveryInQty + (d.TotalDeliveryInQty || 0),
            PendingQty: acc.PendingQty + (d.PendingQty || 0),
            TotalGoodQty: acc.TotalGoodQty + (d.TotalGoodQty || 0),
            TotalDamagedQty: acc.TotalDamagedQty + (d.TotalDamagedQty || 0),
            TotalStock: acc.TotalStock + (d.TotalStock || 0),
        }), { TotalDeliveryOutQty: 0, TotalDeliveryInQty: 0, PendingQty: 0, TotalGoodQty: 0, TotalDamagedQty: 0, TotalStock: 0 });

        const totalRow = worksheet.addRow([
            '', 'Total',
            totals.TotalDeliveryOutQty,
            totals.TotalDeliveryInQty,
            totals.PendingQty,
            totals.TotalGoodQty,
            totals.TotalDamagedQty,
            totals.TotalStock
        ]);
        totalRow.eachCell((cell, colNumber) => {
            cell.font = { bold: true };
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFe0e7ff' } };
            cell.border = thinBorder;
            cell.alignment = { vertical: 'middle', horizontal: colNumber <= 2 ? 'left' : 'right' };
            if (colNumber > 2) cell.numFmt = '#,##0';
        });

        worksheet.columns = [
            { width: 8 },   // Sl.No
            { width: 35 },  // Name
            { width: 15 },  // Opening Stock
            { width: 20 },  // Total Delivery Out Qty
            { width: 15 },  // Pending Qty
            { width: 20 },  // Total Delivery In Qty
            { width: 15 },  // Total Good Qty
            { width: 18 },  // Total Damaged Qty
            { width: 15 }   // Total Stock
        ];

        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer]), fileName);
    };

    return (
        <div style={{
            minWidth: '100%',
            background: '#f5f7fa',
            padding: isMobile ? '8px' : '10px',
            position: 'relative',
            paddingBottom: isMobile ? '80px' : '10px'
        }}>
            {/* Loading Overlay */}
            {loading && (
                <Box
                    sx={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                        zIndex: 9999,
                        backdropFilter: 'blur(4px)'
                    }}
                >
                    <Box sx={{ textAlign: 'center' }}>
                        <CircularProgress
                            size={60}
                            thickness={4}
                            sx={{
                                color: '#667eea',
                                mb: 2
                            }}
                        />
                        <Typography
                            variant="h6"
                            sx={{
                                color: '#667eea',
                                fontWeight: 600,
                                fontSize: '1rem'
                            }}
                        >
                            {loadingMessage}
                        </Typography>
                    </Box>
                </Box>
            )}

            <div style={{
                maxWidth: '1400px',
                margin: '0 auto',
                background: 'white',
                borderRadius: '12px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                padding: isMobile ? '8px' : '12px',
                position: 'relative',
                color: 'black',
                fontFamily: 'Times New Roman',
            }}>
                {/* Header Section */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '10px',
                    paddingBottom: '8px',
                    borderBottom: '1px solid #f0f0f0',
                    flexDirection: isMobile ? 'column' : 'row',
                    gap: isMobile ? '8px' : '0'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', width: isMobile ? '100%' : 'auto' }}>
                        <div style={{ width: '100%' }}>
                            <h4 style={{
                                fontSize: isMobile ? '15px' : '17px',
                                fontWeight: '700',
                                margin: 0,
                                letterSpacing: '-0.3px',
                                fontFamily: 'Times New Roman',
                                textTransform: 'uppercase',
                                textAlign: isMobile ? 'center' : 'left'
                            }}>
                                <span style={{ color: 'black', fontFamily: 'Times New Roman' }}>Delivery Stock Report</span>
                            </h4>
                        </div>
                    </div>
                </div>

                {/* Filters Row */}
                <div style={{
                    marginBottom: '12px',
                    overflowX: 'auto',
                    overflowY: 'hidden',
                    WebkitOverflowScrolling: 'touch',
                    scrollbarWidth: 'thin'
                }}>
                    <div style={{
                        display: 'flex',
                        gap: '8px',
                        alignItems: 'flex-end',
                        minWidth: 'fit-content',
                        paddingBottom: '4px',
                        flexWrap: 'nowrap'
                    }}>
                        {/* From Date */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: isMobile ? '120px' : '140px', flex: isMobile ? '1' : 'none' }}>
                            <label style={{ fontSize: '10px', color: '#666', paddingLeft: '4px' }}>From Date</label>
                            <input
                                type="date"
                                value={fromDate}
                                onChange={handleFromDateChange}
                                style={{
                                    width: '100%',
                                    borderRadius: '6px',
                                    backgroundColor: '#f8f9fa',
                                    fontSize: isMobile ? '10px' : '11px',
                                    height: '32px',
                                    border: '1px solid #d1d5db',
                                    padding: isMobile ? '8px 6px' : '8px 12px'
                                }}
                            />
                        </div>

                        {/* To Date */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: isMobile ? '120px' : '140px', flex: isMobile ? '1' : 'none' }}>
                            <label style={{ fontSize: '10px', color: '#666', paddingLeft: '4px' }}>To Date</label>
                            <input
                                type="date"
                                value={toDate}
                                onChange={handleToDateChange}
                                style={{
                                    width: '100%',
                                    borderRadius: '6px',
                                    backgroundColor: '#f8f9fa',
                                    fontSize: isMobile ? '10px' : '11px',
                                    height: '32px',
                                    border: '1px solid #d1d5db',
                                    padding: isMobile ? '8px 6px' : '8px 12px'
                                }}
                            />
                        </div>

                        {/* Search Input */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: isMobile ? '140px' : '200px', flex: isMobile ? '1' : 'none' }}>
                            <label style={{ fontSize: '10px', color: '#666', paddingLeft: '4px' }}>Search</label>
                            <input
                                type="text"
                                placeholder="Search..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                style={{
                                    width: '100%',
                                    borderRadius: '6px',
                                    backgroundColor: '#f8f9fa',
                                    fontSize: isMobile ? '10px' : '11px',
                                    height: '32px',
                                    border: '1px solid #d1d5db',
                                    padding: isMobile ? '8px 6px' : '8px 12px'
                                }}
                            />
                        </div>

                        {/* Buttons */}
                        {isMobile ? (
                            <>
                                {/* Refresh Icon Button - Mobile */}
                                <Tooltip title="Refresh">
                                    <IconButton
                                        onClick={fetchTableData}
                                        style={{
                                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                            color: 'white',
                                            width: '32px',
                                            height: '32px',
                                            boxShadow: '0 2px 8px rgba(102, 126, 234, 0.4)',
                                            padding: '4px'
                                        }}
                                    >
                                        <Refresh fontSize="small" />
                                    </IconButton>
                                </Tooltip>

                                {/* Export Icon Button - Mobile */}
                                <Tooltip title="Export">
                                    <IconButton
                                        onClick={handleExportExcel}
                                        style={{
                                            background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                                            color: 'white',
                                            width: '32px',
                                            height: '32px',
                                            boxShadow: '0 2px 8px rgba(245, 158, 11, 0.4)',
                                            padding: '4px'
                                        }}
                                    >
                                        <FileDownload fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </>
                        ) : (
                            <>
                                {/* Refresh Button - Desktop */}
                                <Button
                                    onClick={fetchTableData}
                                    style={{
                                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                        color: 'white',
                                        padding: '4px 12px',
                                        borderRadius: '6px',
                                        fontSize: '12px',
                                        fontWeight: '700',
                                        boxShadow: '0 2px 8px rgba(102, 126, 234, 0.4)',
                                        border: 'none',
                                        height: '32px',
                                        textTransform: 'none',
                                        letterSpacing: '0.5px',
                                        minWidth: '100px'
                                    }}
                                >
                                    Refresh
                                </Button>

                                {/* Excel Button - Desktop */}
                                <Button
                                    onClick={handleExportExcel}
                                    style={{
                                        background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                                        color: 'white',
                                        padding: '4px 12px',
                                        borderRadius: '6px',
                                        fontSize: '12px',
                                        fontWeight: '700',
                                        boxShadow: '0 2px 8px rgba(245, 158, 11, 0.4)',
                                        border: 'none',
                                        height: '32px',
                                        textTransform: 'none',
                                        letterSpacing: '0.5px',
                                        minWidth: '100px'
                                    }}
                                >
                                    Export
                                </Button>
                            </>
                        )}
                    </div>
                </div>

                {/* Table Section */}
                <div style={{ marginBottom: isMobile ? '80px' : '8px' }}>
                    <TableContainer component={Paper} style={{
                        boxShadow: '0 1px 4px rgba(0, 0, 0, 0.06)',
                        borderRadius: '6px',
                        overflow: isMobile ? 'auto' : 'hidden'
                    }}>
                        <Table sx={{ minWidth: isMobile ? 900 : 650 }} size="small" aria-label="delivery stock report table">
                            <TableHead>
                                <TableRow style={{
                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                    height: '24px'
                                }}>
                                    {['Sl.No', 'Name', 'Delivery Out', 'Delivery In', 'Pending', 'Good Qty', 'Damaged', 'Total Stock'].map((col) => (
                                        <TableCell
                                            key={col}
                                            style={{
                                                color: 'white',
                                                fontWeight: '600',
                                                fontSize: isMobile ? '8px' : '9px',
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.2px',
                                                padding: isMobile ? '2px 4px' : '2px 6px',
                                                textAlign: col === 'Sl.No' || col === 'Name' ? 'left' : 'right',
                                                height: '24px'
                                            }}
                                        >
                                            {col}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {filteredDataItem.map((data, index) => (
                                    <TableRow
                                        key={index}
                                        sx={{
                                            '&:nth-of-type(even)': { backgroundColor: '#fafbfc' },
                                            '&:hover': {
                                                backgroundColor: '#f1f5f9',
                                                transition: 'all 0.15s ease-in-out'
                                            },
                                            borderBottom: '1px solid #f1f5f9',
                                            height: '28px'
                                        }}
                                    >

                                        <TableCell sx={{ py: 0.5, px: isMobile ? 0.5 : 0.75 }}>
                                            <Typography variant="body2" sx={{ fontSize: isMobile ? '0.65rem' : '0.7rem', fontWeight: 500, color: '#374151' }}>
                                                {index + 1}
                                            </Typography>
                                        </TableCell>

                                        <TableCell sx={{ py: 0.5, px: isMobile ? 0.5 : 0.75 }}>
                                            <Typography variant="body2" sx={{ fontSize: isMobile ? '0.65rem' : '0.7rem', color: '#374151' }}>
                                                {data.Name}
                                            </Typography>
                                        </TableCell>

                                        <TableCell sx={{ py: 0.5, px: isMobile ? 0.5 : 0.75, textAlign: 'right' }}>
                                            <Typography variant="body2" sx={{ fontSize: isMobile ? '0.65rem' : '0.7rem', fontWeight: 600, color: '#dc2626' }}>
                                                {data.TotalDeliveryOutQty != null ? data.TotalDeliveryOutQty.toLocaleString('en-IN') : '-'}
                                            </Typography>
                                        </TableCell>

                                        <TableCell sx={{ py: 0.5, px: isMobile ? 0.5 : 0.75, textAlign: 'right' }}>
                                            <Typography variant="body2" sx={{ fontSize: isMobile ? '0.65rem' : '0.7rem', fontWeight: 600, color: '#059669' }}>
                                                {data.TotalDeliveryInQty != null ? data.TotalDeliveryInQty.toLocaleString('en-IN') : '-'}
                                            </Typography>
                                        </TableCell>

                                        <TableCell sx={{ py: 0.5, px: isMobile ? 0.5 : 0.75, textAlign: 'right' }}>
                                            <Typography variant="body2" sx={{ fontSize: isMobile ? '0.65rem' : '0.7rem', fontWeight: 600, color: '#f59e0b' }}>
                                                {data.PendingQty != null ? data.PendingQty.toLocaleString('en-IN') : '-'}
                                            </Typography>
                                        </TableCell>

                                        <TableCell sx={{ py: 0.5, px: isMobile ? 0.5 : 0.75, textAlign: 'right' }}>
                                            <Typography variant="body2" sx={{ fontSize: isMobile ? '0.65rem' : '0.7rem', fontWeight: 600, color: '#10b981' }}>
                                                {data.TotalGoodQty != null ? data.TotalGoodQty.toLocaleString('en-IN') : '-'}
                                            </Typography>
                                        </TableCell>

                                        <TableCell sx={{ py: 0.5, px: isMobile ? 0.5 : 0.75, textAlign: 'right' }}>
                                            <Typography variant="body2" sx={{ fontSize: isMobile ? '0.65rem' : '0.7rem', fontWeight: 600, color: '#ef4444' }}>
                                                {data.TotalDamagedQty != null ? data.TotalDamagedQty.toLocaleString('en-IN') : '-'}
                                            </Typography>
                                        </TableCell>

                                        <TableCell sx={{ py: 0.5, px: isMobile ? 0.5 : 0.75, textAlign: 'right' }}>
                                            <Typography variant="body2" sx={{ fontSize: isMobile ? '0.65rem' : '0.7rem', fontWeight: 700, color: '#1e40af' }}>
                                                {data.TotalStock != null ? data.TotalStock.toLocaleString('en-IN') : '-'}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {filteredDataItem.length > 0 && (() => {
                                    const totals = filteredDataItem.reduce((acc, d) => ({
                                        TotalDeliveryOutQty: acc.TotalDeliveryOutQty + (d.TotalDeliveryOutQty || 0),
                                        TotalDeliveryInQty: acc.TotalDeliveryInQty + (d.TotalDeliveryInQty || 0),
                                        PendingQty: acc.PendingQty + (d.PendingQty || 0),
                                        TotalGoodQty: acc.TotalGoodQty + (d.TotalGoodQty || 0),
                                        TotalDamagedQty: acc.TotalDamagedQty + (d.TotalDamagedQty || 0),
                                        TotalStock: acc.TotalStock + (d.TotalStock || 0),
                                    }), { TotalDeliveryOutQty: 0, TotalDeliveryInQty: 0, PendingQty: 0, TotalGoodQty: 0, TotalDamagedQty: 0, TotalStock: 0 });
                                    const cellSx = { py: 0.5, px: isMobile ? 0.5 : 0.75, textAlign: 'right', borderTop: '2px solid #667eea' };
                                    const typoSx = { fontSize: isMobile ? '0.65rem' : '0.7rem', fontWeight: 700 };
                                    return (
                                        <TableRow sx={{ backgroundColor: '#eef2ff', height: '28px' }}>
                                            <TableCell sx={{ py: 0.5, px: isMobile ? 0.5 : 0.75, borderTop: '2px solid #667eea' }} colSpan={2}>
                                                <Typography variant="body2" sx={{ ...typoSx, color: '#374151' }}>Total</Typography>
                                            </TableCell>
                                            <TableCell sx={cellSx}>
                                                <Typography variant="body2" sx={{ ...typoSx, color: '#dc2626' }}>{totals.TotalDeliveryOutQty.toLocaleString('en-IN')}</Typography>
                                            </TableCell>
                                            <TableCell sx={cellSx}>
                                                <Typography variant="body2" sx={{ ...typoSx, color: '#059669' }}>{totals.TotalDeliveryInQty.toLocaleString('en-IN')}</Typography>
                                            </TableCell>
                                            <TableCell sx={cellSx}>
                                                <Typography variant="body2" sx={{ ...typoSx, color: '#f59e0b' }}>{totals.PendingQty.toLocaleString('en-IN')}</Typography>
                                            </TableCell>
                                            <TableCell sx={cellSx}>
                                                <Typography variant="body2" sx={{ ...typoSx, color: '#10b981' }}>{totals.TotalGoodQty.toLocaleString('en-IN')}</Typography>
                                            </TableCell>
                                            <TableCell sx={cellSx}>
                                                <Typography variant="body2" sx={{ ...typoSx, color: '#ef4444' }}>{totals.TotalDamagedQty.toLocaleString('en-IN')}</Typography>
                                            </TableCell>
                                            <TableCell sx={cellSx}>
                                                <Typography variant="body2" sx={{ ...typoSx, color: '#1e40af' }}>{totals.TotalStock.toLocaleString('en-IN')}</Typography>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })()}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </div>
            </div>
        </div>
    );
}

export default DeliveryStockReport;
